import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatNum } from "@/lib/format";
import {
  Package, Warehouse, ArrowRight, Plus, Search, GlassWater, TruckIcon,
  AlertTriangle, RefreshCw, Clock, Camera, ExternalLink, X as XIcon, Trash2,
} from "lucide-react";
import { getUser } from "@/lib/auth";
import { roleDisplayLabel } from "@/lib/auth";

const DEFAULT_ITEMS = [
  { code: "BIB", label: "BIB", custom: false },
  { code: "CO2", label: "CO2", custom: false },
  { code: "Cups", label: "Cups", custom: false },
  { code: "Water Jar", label: "Water Jar", custom: false },
  { code: "Promotional Items", label: "Promotional Items", custom: false },
  { code: "Kiosk", label: "Kiosk", custom: false },
  { code: "POS Machine", label: "POS Machine", custom: false },
  { code: "Other", label: "Other", custom: true },
];
// Every canonical code in the master item list — used by KPI tiles and the table's per-item cells.
// Keep in sync with DEFAULT_ITEMS (and with backend STOCK_ITEMS on server.py). "Other" is the rollup for anything not in this list.
const CANONICAL_CODES = ["BIB", "CO2", "Cups", "Water Jar", "Promotional Items", "Kiosk", "POS Machine"];

export default function AdminStock() {
  const me = getUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [zoneFilter, setZoneFilter] = useState("");
  const [zones, setZones] = useState([]);
  const [movements, setMovements] = useState([]);
  const [itemDefs, setItemDefs] = useState(DEFAULT_ITEMS);

  const [receiptForm, setReceiptForm] = useState(null);
  const [dispatchForm, setDispatchForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // movement object pending delete
  const canDeleteMovements = me?.role === "Admin" || me?.role === "SuperAdmin";

  // Task 61 — Movements tab: server-side pagination + filters. UI state only; no data mutation.
  const MOV_PAGE_SIZE = 50;
  const [movTab, setMovTab] = useState("summary");
  const [movPage, setMovPage] = useState(0);
  const [movTotal, setMovTotal] = useState(0);
  const [movLoading, setMovLoading] = useState(false);
  const [movFilters, setMovFilters] = useState({
    store_id: "", item: "", type: "", actor_id: "", from_date: "", to_date: "",
  });
  const [movFilterOpts, setMovFilterOpts] = useState({ items: [], actors: [] });
  const setFilter = (patch) => { setMovFilters(f => ({ ...f, ...patch })); setMovPage(0); };

  const deleteMovement = async (m) => {
    setSaving(true);
    try {
      await api.delete(`/inventory/movements/${m.id}`);
      toast.success(`Deleted ${m.type} · ${m.item} ${m.units}u${m.store_name ? " @ " + m.store_name : ""}`);
      setConfirmDelete(null);
      await Promise.all([load(), loadMovements(), loadMovementFilters()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete movement");
    } finally { setSaving(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: sum }, { data: zs }, { data: cfg }] = await Promise.all([
        api.get("/inventory/summary"),
        api.get("/zones"),
        api.get("/config"),
      ]);
      setData(sum); setZones(zs);
      if (cfg.stock_items && cfg.stock_items.length) {
        setItemDefs(cfg.stock_items.map(i => ({ code: i.code, label: i.label, custom: !!i.custom })));
      }
    } catch (e) { toast.error("Failed to load inventory"); }
    finally { setLoading(false); }
  };

  // Task 61 — Movements list: server-side filters + pagination. Response shape is
  // {items, total}; keep a defensive fallback so a shape mismatch never blanks the table.
  const loadMovements = async () => {
    setMovLoading(true);
    try {
      const params = { limit: MOV_PAGE_SIZE, skip: movPage * MOV_PAGE_SIZE };
      if (movFilters.store_id) params.store_id = movFilters.store_id;
      if (movFilters.item) params.item = movFilters.item;
      if (movFilters.type) params.type = movFilters.type;
      if (movFilters.actor_id) params.actor_id = movFilters.actor_id;
      if (movFilters.from_date) params.from_date = movFilters.from_date;
      if (movFilters.to_date) params.to_date = movFilters.to_date;
      const res = await api.get("/inventory/movements", { params });
      const rows = Array.isArray(res.data) ? res.data : (res.data.items || []);
      const total = Array.isArray(res.data) ? rows.length : (res.data.total ?? rows.length);
      setMovements(rows); setMovTotal(total);
    } catch (e) { toast.error("Failed to load movements"); }
    finally { setMovLoading(false); }
  };

  const loadMovementFilters = async () => {
    try {
      const { data } = await api.get("/inventory/movement-filters");
      setMovFilterOpts({ items: data.items || [], actors: data.actors || [] });
    } catch (e) { /* silent — filters just stay empty */ }
  };
  useEffect(() => { load(); loadMovementFilters(); }, []);
  // Movements refetch whenever filters or page change. Keeps the count in lock-step
  // with the visible rows (both come from the same server query).
  useEffect(() => { loadMovements(); /* eslint-disable-next-line */ }, [movPage, movFilters]);

  // Resolve payload for either "Other" (custom name) or a canonical item
  const resolveItemPayload = (form) => {
    if (form.item === "Other") {
      const name = (form.custom_item_name || "").trim();
      if (!name) throw new Error("Please type the Material name for 'Other'");
      return { item: "Other", custom_item_name: name, label: name };
    }
    return { item: form.item, label: form.item };
  };

  const submitReceipt = async () => {
    if (!(Number(receiptForm.units) > 0)) return toast.error("Units must be > 0");
    let resolved;
    try { resolved = resolveItemPayload(receiptForm); } catch (e) { return toast.error(e.message); }
    setSaving(true);
    try {
      await api.post("/inventory/receipt", {
        item: resolved.item, custom_item_name: resolved.custom_item_name,
        units: Number(receiptForm.units),
        date: receiptForm.date || undefined,
        invoice_ref: receiptForm.invoice_ref, notes: receiptForm.notes,
      });
      toast.success(`+${receiptForm.units} ${resolved.label} into central stock`);
      setReceiptForm(null); load(); loadMovements(); loadMovementFilters();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const submitDispatch = async () => {
    if (!dispatchForm.store_id) return toast.error("Store required");
    if (!(Number(dispatchForm.units) > 0)) return toast.error("Units must be > 0");
    let resolved;
    try { resolved = resolveItemPayload(dispatchForm); } catch (e) { return toast.error(e.message); }
    setSaving(true);
    try {
      await api.post("/inventory/dispatch", {
        store_id: dispatchForm.store_id, item: resolved.item,
        custom_item_name: resolved.custom_item_name,
        units: Number(dispatchForm.units),
        date: dispatchForm.date || undefined,
        notes: dispatchForm.notes,
        photo_path: dispatchForm.photo_path || null,
      });
      toast.success(`${dispatchForm.units} ${resolved.label} dispatched`);
      setDispatchForm(null); load(); loadMovements(); loadMovementFilters();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const uploadDispatchPhoto = async (file) => {
    if (!file || !dispatchForm) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image too large (max 5 MB)");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setDispatchForm(f => ({ ...f, photo_path: data.path, photo_name: file.name }));
      toast.success("Photo attached");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const openPhoto = (path) => {
    if (!path) return;
    const token = localStorage.getItem("bb_token");
    setPhotoPreview(`${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${token}`);
  };

  const rows = useMemo(() => {
    if (!data) return [];
    return data.stores.filter(r => {
      if (zoneFilter && r.zone !== zoneFilter) return false;
      if (q && !`${r.store_name} ${r.store_code} ${r.city}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [data, q, zoneFilter]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title="Stock & Dispatch" subtitle="Central warehouse · Store on-hand · Restock" />
      <div className="flex-1 flex">
        <SideNav />
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 space-y-4">

          {/* Central stock KPIs — all canonical item types + Other rollup.
              Grid wraps to two rows on medium screens so labels/values never truncate after Water Jar / Promotional Items were added. */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {[...CANONICAL_CODES, "Other"].map((code) => {
              const units = data ? (data.central_by_type?.[code] ?? 0) : null;
              let sub = "";
              if (data && code === "BIB") sub = `${formatNum(data.central_glasses.BIB)} cups of headroom`;
              else if (data && code === "CO2") sub = `${formatNum(data.central_glasses.CO2)} cups of headroom`;
              else if (data && code === "Other") {
                const br = data.central_other_breakdown || {};
                const names = Object.keys(br);
                sub = names.length ? names.slice(0, 3).map(n => `${n} ${br[n]}u`).join(" · ") + (names.length > 3 ? ` +${names.length - 3}` : "") : "no custom items";
              } else sub = "in central";
              const color = code === "BIB" ? "text-brand-red" : code === "CO2" ? "text-blue-700" : "text-gray-800";
              return (
                <Kpi key={code} label={`Central ${code}`}
                  value={units != null ? `${units} u` : "—"}
                  sub={sub} color={color} icon={Warehouse}
                  testId={`central-kpi-${code.replace(/\s+/g, "-").toLowerCase()}`} />
              );
            })}
          </div>

          {/* Secondary KPIs — "Total dispatched" broken out per canonical item so long labels never truncate */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card className="rounded-xl border-gray-200 bg-white p-4" data-testid="dispatched-total-kpi">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[11px] uppercase font-bold tracking-[0.14em] text-gray-500">Total dispatched</div>
                  <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs font-mono">
                    {data ? [...CANONICAL_CODES, "Other"].map(c => (
                      <div key={c} className="flex items-baseline justify-between gap-2">
                        <span className="text-gray-500 truncate">{c}</span>
                        <span className="font-semibold">{data.dispatched_by_type?.[c] ?? 0} u</span>
                      </div>
                    )) : <div className="text-gray-400">—</div>}
                  </div>
                  {data && (
                    <div className="text-[11px] text-gray-500 mt-2">
                      {formatNum((data.dispatched_glasses_total.BIB || 0) + (data.dispatched_glasses_total.CO2 || 0))} cups (BIB+CO2 yield)
                    </div>
                  )}
                </div>
                <TruckIcon className="h-5 w-5 text-gray-300 shrink-0" />
              </div>
            </Card>
            <Kpi label="Low-stock stores" value={data ? String(data.low_stock_count) : "—"}
              sub="need restock now" color={data && data.low_stock_count > 0 ? "text-red-700" : "text-green-700"} icon={AlertTriangle}
              testId="low-stock-kpi" />
          </div>

          {/* Action bar */}
          <Card className="rounded-xl border-gray-200 bg-white p-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[220px] max-w-sm">
                <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="stock-search" placeholder="Search store, code, city…" value={q}
                  onChange={e => setQ(e.target.value)} className="pl-9" />
              </div>
              <select data-testid="stock-zone-filter" value={zoneFilter} onChange={e => setZoneFilter(e.target.value)}
                className="h-10 rounded-md border border-gray-200 px-3 text-sm bg-white">
                <option value="">All zones</option>
                {zones.map(z => <option key={z.id} value={z.name}>{z.name}</option>)}
              </select>
              <div className="ml-auto flex items-center gap-2">
                <Button data-testid="stock-refresh-btn" variant="outline" onClick={() => { load(); loadMovements(); loadMovementFilters(); }}>
                  <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                </Button>
                {/* ITEM B.11 (2026-02-29) — Coordinator (Supervisor) + Manager (Project Lead) + IT
                    can now Receive Stock the same way Admin does. Backend gate widened accordingly. */}
                {["Admin", "SuperAdmin", "Supervisor", "Project Lead", "IT"].includes(me?.role) && (
                  <Button data-testid="add-receipt-btn" variant="outline"
                    onClick={() => setReceiptForm({ item: "BIB", custom_item_name: "", units: "", date: new Date().toISOString().slice(0,10), invoice_ref: "", notes: "" })}>
                    <Plus className="h-4 w-4 mr-2" /> Receive stock
                  </Button>
                )}
                <Button data-testid="add-dispatch-btn"
                  onClick={() => setDispatchForm({ item: "BIB", custom_item_name: "", store_id: rows[0]?.store_id || "", units: "", date: new Date().toISOString().slice(0,10), notes: "" })}
                  className="bg-brand-red hover:bg-[#B31721]">
                  <TruckIcon className="h-4 w-4 mr-2" /> Dispatch to store
                </Button>
              </div>
            </div>
          </Card>

          {/* Store rows */}
          <Tabs value={movTab} onValueChange={setMovTab} className="w-full">
            <TabsList className="mb-3" data-testid="stock-tabs">
              <TabsTrigger value="summary" data-testid="stock-tab-summary">Summary</TabsTrigger>
              <TabsTrigger value="movements" data-testid="stock-tab-movements">
                Movements{movTotal > 0 ? ` (${movTotal})` : ""}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="summary" className="mt-0">
          <Card className="rounded-xl border-gray-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="stock-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-3">Store · Zone</th>
                    <th className="text-right px-4 py-3">Dispatched (all time)</th>
                    <th className="text-right px-4 py-3">On hand</th>
                    <th className="text-right px-4 py-3">Cups left</th>
                    <th className="text-right px-4 py-3">Days cover</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-right px-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={7} className="text-center py-8 text-gray-500">Loading…</td></tr>}
                  {!loading && rows.map(r => (
                    <tr key={r.store_id} className={`border-t border-gray-100 ${r.low_stock ? "bg-red-50/40" : ""}`}
                        data-testid={`stock-row-${r.store_no}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{r.store_name}</div>
                        <div className="text-xs text-gray-500">{r.city} · {r.zone || "—"} · {r.store_code}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-[11px] leading-tight">
                        {CANONICAL_CODES.map(c => {
                          const u = r.per_item_dispatched?.[c] ?? 0;
                          if (!u && c !== "BIB" && c !== "CO2") return null;
                          return <div key={c}>{c} {u} u</div>;
                        })}
                        {r.per_item_dispatched?.Other > 0 && (
                          <div className="text-gray-500">
                            Other {r.per_item_dispatched.Other} u
                            {r.other_dispatched_breakdown && Object.keys(r.other_dispatched_breakdown).length > 0 && (
                              <div className="text-[10px] text-gray-400">
                                {Object.entries(r.other_dispatched_breakdown).slice(0, 2).map(([n, v]) => `${n} ${v}`).join(" · ")}
                                {Object.keys(r.other_dispatched_breakdown).length > 2 ? ` +${Object.keys(r.other_dispatched_breakdown).length - 2}` : ""}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-[11px] leading-tight">
                        <div>BIB {r.bib_on_hand != null ? `${r.bib_on_hand} u` : "—"}</div>
                        <div>CO2 {r.co2_on_hand != null ? `${r.co2_on_hand} u` : "—"}</div>
                        {CANONICAL_CODES.filter(c => c !== "BIB" && c !== "CO2").map(c => {
                          const u = r.per_item_dispatched?.[c] ?? 0;
                          if (!u) return null;
                          return <div key={c} className="text-gray-500">{c} {u} u</div>;
                        })}
                        {r.per_item_dispatched?.Other > 0 && (
                          <div className="text-gray-500">Other {r.per_item_dispatched.Other} u</div>
                        )}
                      </td>
                      <td className={`px-4 py-3 text-right ${r.low_stock ? "text-red-700 font-bold" : "text-gray-800"}`}>
                        <div className="font-mono text-sm font-semibold">
                          {r.glasses_left != null ? formatNum(r.glasses_left) : "—"}
                        </div>
                        <div className="text-[10px] text-gray-500 font-normal">
                          BIB {r.bib_glasses_left != null ? formatNum(r.bib_glasses_left) : "—"} · CO2 {r.co2_glasses_left != null ? formatNum(r.co2_glasses_left) : "—"}
                        </div>
                      </td>
                      <td className={`px-4 py-3 text-right font-mono ${r.days_of_cover != null && r.days_of_cover < 3 ? "text-red-700" : "text-gray-700"}`}>
                        {r.days_of_cover != null ? `${r.days_of_cover}d` : "—"}
                        {r.avg_daily_sales > 0 && <div className="text-[10px] text-gray-400 font-normal">avg {r.avg_daily_sales}/day</div>}
                      </td>
                      <td className="px-4 py-3">
                        {r.low_stock ? (
                          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                            <AlertTriangle className="h-3 w-3 mr-1" /> LOW STOCK
                          </Badge>
                        ) : (r.glasses_left != null && r.glasses_left < 500) ? (
                          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Watch</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">OK</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant={r.low_stock ? "default" : "outline"}
                          onClick={() => setDispatchForm({
                            item: r.bib_glasses_left != null && r.bib_glasses_left <= (r.co2_glasses_left ?? Infinity) ? "BIB" : "CO2",
                            store_id: r.store_id, units: "", notes: "",
                          })}
                          className={r.low_stock ? "bg-brand-red hover:bg-[#B31721] text-white" : ""}
                          data-testid={`restock-${r.store_no}`}>
                          <TruckIcon className="h-3.5 w-3.5 mr-1" /> Restock
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!loading && rows.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-8 text-gray-500">No stores match</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
            </TabsContent>

            <TabsContent value="movements" className="mt-0 space-y-3">
              {/* Movement filters — Store · Item · Type · Who · From · To. Any change resets to page 1. */}
              <Card className="rounded-xl border-gray-200 bg-white p-3" data-testid="mov-filter-bar">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                  <select data-testid="mov-filter-store" value={movFilters.store_id}
                    onChange={e => setFilter({ store_id: e.target.value })}
                    className="h-9 rounded-md border border-gray-200 px-2 text-xs bg-white">
                    <option value="">All stores</option>
                    {data?.stores?.map(s => (
                      <option key={s.store_id} value={s.store_id}>{s.store_name} · {s.city}</option>
                    ))}
                  </select>
                  <select data-testid="mov-filter-item" value={movFilters.item}
                    onChange={e => setFilter({ item: e.target.value })}
                    className="h-9 rounded-md border border-gray-200 px-2 text-xs bg-white">
                    <option value="">All items</option>
                    {movFilterOpts.items.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                  <select data-testid="mov-filter-type" value={movFilters.type}
                    onChange={e => setFilter({ type: e.target.value })}
                    className="h-9 rounded-md border border-gray-200 px-2 text-xs bg-white">
                    <option value="">All types</option>
                    <option value="dispatch">Dispatch</option>
                    <option value="receipt">Receipt</option>
                  </select>
                  <select data-testid="mov-filter-actor" value={movFilters.actor_id}
                    onChange={e => setFilter({ actor_id: e.target.value })}
                    className="h-9 rounded-md border border-gray-200 px-2 text-xs bg-white">
                    <option value="">All users</option>
                    {movFilterOpts.actors.map(a => (
                      <option key={a.id} value={a.id}>{a.name} · {roleDisplayLabel(a.role)}</option>
                    ))}
                  </select>
                  <Input data-testid="mov-filter-from" type="date" value={movFilters.from_date}
                    onChange={e => setFilter({ from_date: e.target.value })} className="h-9 text-xs" />
                  <Input data-testid="mov-filter-to" type="date" value={movFilters.to_date}
                    onChange={e => setFilter({ to_date: e.target.value })} className="h-9 text-xs" />
                </div>
                {(movFilters.store_id || movFilters.item || movFilters.type || movFilters.actor_id || movFilters.from_date || movFilters.to_date) && (
                  <div className="mt-2">
                    <button data-testid="mov-filter-clear" onClick={() => setFilter({ store_id: "", item: "", type: "", actor_id: "", from_date: "", to_date: "" })}
                      className="text-xs text-brand-red font-semibold hover:underline">
                      Clear all filters
                    </button>
                  </div>
                )}
              </Card>

          {/* Recent movements */}
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.14em] text-gray-500 mb-2">Recent Movements</div>
            <Card className="rounded-xl border-gray-200 bg-white overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="movements-table">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="text-left px-4 py-3">When</th>
                      <th className="text-left px-4 py-3">Type</th>
                      <th className="text-left px-4 py-3">Item · Units</th>
                      <th className="text-left px-4 py-3">Store</th>
                      <th className="text-left px-4 py-3">By</th>
                      <th className="text-left px-4 py-3">Proof</th>
                      {canDeleteMovements && <th className="text-right px-4 py-3">Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {movements.length === 0 && (
                      <tr><td colSpan={canDeleteMovements ? 7 : 6} className="text-center py-6 text-gray-500">No movements yet — start by receiving stock</td></tr>
                    )}
                    {movements.map(m => (
                      <tr key={m.id} className="border-t border-gray-100" data-testid={`move-${m.id.slice(0,8)}`}>
                        <td className="px-4 py-3">
                          <div className="text-xs">{m.at.slice(0, 19).replace("T", " ")}</div>
                          <div className="text-[10px] text-gray-400 flex items-center gap-1"><Clock className="h-3 w-3" />{m.date}</div>
                        </td>
                        <td className="px-4 py-3">
                          {m.type === "receipt" && <Badge className="bg-green-100 text-green-800 hover:bg-green-100">+ Receipt</Badge>}
                          {m.type === "dispatch" && <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100"><ArrowRight className="h-3 w-3 mr-1" /> Dispatch</Badge>}
                          {m.type === "adjust" && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Adjust</Badge>}
                        </td>
                        <td className="px-4 py-3 font-mono">
                          {m.item} · {m.units > 0 ? "+" : ""}{m.units} u
                        </td>
                        <td className="px-4 py-3">{m.store_name || <span className="text-gray-400">— central —</span>}</td>
                        <td className="px-4 py-3">
                          <div className="text-sm">{m.actor_name}</div>
                          <div className="text-[10px] uppercase text-gray-500 tracking-wider">{roleDisplayLabel(m.actor_role)}</div>
                        </td>
                        <td className="px-4 py-3">
                          {m.photo_path ? (
                            <button data-testid={`view-proof-${m.id.slice(0,8)}`} onClick={() => openPhoto(m.photo_path)}
                              className="inline-flex items-center gap-1 text-xs text-brand-red font-semibold hover:underline">
                              <Camera className="h-3.5 w-3.5" /> View
                            </button>
                          ) : <span className="text-xs text-gray-400">—</span>}
                        </td>
                        {canDeleteMovements && (
                          <td className="px-4 py-3 text-right">
                            <button
                              data-testid={`delete-move-${m.id.slice(0,8)}`}
                              onClick={() => setConfirmDelete(m)}
                              className="inline-flex items-center gap-1 text-xs text-red-600 font-semibold hover:underline"
                              title="Permanently delete this movement (reverses stock)"
                            >
                              <Trash2 className="h-3.5 w-3.5" /> Delete
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

              {/* Pagination — Prev / Next with total. Total comes from the same server query as the rows. */}
              <div className="flex items-center justify-between text-xs text-gray-600" data-testid="mov-pagination">
                <div>
                  {movTotal > 0 ? (
                    <span>
                      Showing <span className="font-semibold">{movPage * MOV_PAGE_SIZE + 1}</span>–
                      <span className="font-semibold">{Math.min((movPage + 1) * MOV_PAGE_SIZE, movTotal)}</span> of
                      <span className="font-semibold"> {movTotal}</span>
                    </span>
                  ) : (
                    <span className="text-gray-400">{movLoading ? "Loading…" : "No movements match the current filters"}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" data-testid="mov-page-prev"
                    disabled={movPage === 0 || movLoading}
                    onClick={() => setMovPage(p => Math.max(0, p - 1))}>Prev</Button>
                  <span className="font-mono">Page {movPage + 1} / {Math.max(1, Math.ceil(movTotal / MOV_PAGE_SIZE))}</span>
                  <Button size="sm" variant="outline" data-testid="mov-page-next"
                    disabled={movLoading || (movPage + 1) * MOV_PAGE_SIZE >= movTotal}
                    onClick={() => setMovPage(p => p + 1)}>Next</Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </main>
      </div>

      {/* Receipt dialog */}
      {receiptForm && (
        <Dialog open={!!receiptForm} onOpenChange={(v) => !v && setReceiptForm(null)}>
          <DialogContent data-testid="receipt-dialog" className="rounded-2xl max-w-md">
            <DialogHeader><DialogTitle>Receive stock into central</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Item</label>
                <select data-testid="receipt-item" value={receiptForm.item} onChange={e => setReceiptForm({ ...receiptForm, item: e.target.value })}
                  className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                  {itemDefs.map(x => <option key={x.code} value={x.code}>{x.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Units</label>
                <Input data-testid="receipt-units" inputMode="decimal" value={receiptForm.units}
                  onChange={e => setReceiptForm({ ...receiptForm, units: e.target.value.replace(/[^\d.]/g, "") })} />
              </div>
              {receiptForm.item === "Other" && (
                <div className="col-span-2">
                  <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Material name (required)</label>
                  <Input data-testid="recv-other-material"
                    placeholder="e.g. Water Bubble Top, Syrup Pump, Banner, Cleaning Kit"
                    value={receiptForm.custom_item_name || ""}
                    onChange={e => setReceiptForm({ ...receiptForm, custom_item_name: e.target.value })} />
                </div>
              )}
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Date *</label>
                <Input data-testid="receipt-date" type="date" value={receiptForm.date || ""}
                  onChange={e => setReceiptForm({ ...receiptForm, date: e.target.value })} />
                <div className="text-[10px] text-gray-500 mt-1">Back-date if correcting a missed entry.</div>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Invoice reference</label>
                <Input data-testid="receipt-invoice" value={receiptForm.invoice_ref} onChange={e => setReceiptForm({ ...receiptForm, invoice_ref: e.target.value })} placeholder="e.g. INV-2026-014" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Notes</label>
                <Input data-testid="receipt-notes" value={receiptForm.notes} onChange={e => setReceiptForm({ ...receiptForm, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReceiptForm(null)}>Cancel</Button>
              <Button data-testid="save-receipt-btn" onClick={submitReceipt}
                disabled={saving || (receiptForm.item === "Other" && !(receiptForm.custom_item_name || "").trim())}
                className="bg-brand-red hover:bg-[#B31721]">
                <Plus className="h-4 w-4 mr-2" /> Receive
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Dispatch dialog */}
      {dispatchForm && (
        <Dialog open={!!dispatchForm} onOpenChange={(v) => !v && setDispatchForm(null)}>
          <DialogContent data-testid="dispatch-dialog" className="rounded-2xl max-w-md">
            <DialogHeader><DialogTitle>Dispatch stock to store</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Store</label>
                {(data?.stores || []).length === 0 ? (
                  <div data-testid="dispatch-no-stores" className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                    No stores assigned to you. Ask your Admin to assign a store before dispatching stock.
                  </div>
                ) : (
                  <select data-testid="dispatch-store" value={dispatchForm.store_id} onChange={e => setDispatchForm({ ...dispatchForm, store_id: e.target.value })}
                    className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                    {(data?.stores || []).map(s => (
                      <option key={s.store_id} value={s.store_id}>
                        {s.store_name} · {s.zone || s.city}{s.low_stock ? " ⚠ LOW" : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Item</label>
                <select data-testid="dispatch-item" value={dispatchForm.item} onChange={e => setDispatchForm({ ...dispatchForm, item: e.target.value })}
                  className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                  {itemDefs.map(x => <option key={x.code} value={x.code}>{x.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Units</label>
                <Input data-testid="dispatch-units" inputMode="decimal" value={dispatchForm.units}
                  onChange={e => setDispatchForm({ ...dispatchForm, units: e.target.value.replace(/[^\d.]/g, "") })} />
              </div>
              {dispatchForm.item === "Other" && (
                <div className="col-span-2">
                  <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Material name (required)</label>
                  <Input data-testid="dispatch-custom-name"
                    placeholder="e.g. Water Bubble Top, Syrup Pump, Banner, Cleaning Kit"
                    value={dispatchForm.custom_item_name || ""}
                    onChange={e => setDispatchForm({ ...dispatchForm, custom_item_name: e.target.value })} />
                </div>
              )}
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Date *</label>
                <Input data-testid="dispatch-date" type="date" value={dispatchForm.date || ""}
                  onChange={e => setDispatchForm({ ...dispatchForm, date: e.target.value })} />
                <div className="text-[10px] text-gray-500 mt-1">Back-date if correcting a missed entry.</div>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Notes</label>
                <Input data-testid="dispatch-notes" value={dispatchForm.notes} onChange={e => setDispatchForm({ ...dispatchForm, notes: e.target.value })} placeholder="e.g. Restock following low-stock alert" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Delivery photo (optional)</label>
                {dispatchForm.photo_path ? (
                  <div className="flex items-center gap-2 rounded-md bg-green-50 border border-green-200 p-2">
                    <Camera className="h-4 w-4 text-green-700" />
                    <span className="text-xs text-green-800 flex-1 truncate">{dispatchForm.photo_name || "Photo attached"}</span>
                    <Button size="sm" variant="ghost" onClick={() => openPhoto(dispatchForm.photo_path)}>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDispatchForm(f => ({ ...f, photo_path: null, photo_name: null }))}>
                      <XIcon className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input data-testid="dispatch-photo-input" type="file"
                      accept="image/*" capture="environment"
                      onChange={e => uploadDispatchPhoto(e.target.files?.[0])}
                      disabled={uploading} className="flex-1" />
                    {uploading && <span className="text-xs text-gray-500">Uploading…</span>}
                  </div>
                )}
                <div className="text-[11px] text-gray-500 mt-1">Snap the crate/pouch on delivery so the store owner sees proof.</div>
              </div>
              {data && (
                <div className="col-span-2 text-xs rounded-md bg-gray-50 border border-gray-200 p-2 text-gray-700">
                  Central has <span className="font-mono font-bold">
                    {(() => {
                      const label = dispatchForm.item === "Other" ? (dispatchForm.custom_item_name || "").trim() : dispatchForm.item;
                      const avail = (data.central && label in data.central) ? data.central[label] : 0;
                      return `${avail} u ${label || "—"}`;
                    })()}
                  </span> available.
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDispatchForm(null)}>Cancel</Button>
              <Button data-testid="save-dispatch-btn" onClick={submitDispatch}
                disabled={saving || (data?.stores || []).length === 0 || (dispatchForm.item === "Other" && !(dispatchForm.custom_item_name || "").trim())}
                className="bg-brand-red hover:bg-[#B31721]">
                <TruckIcon className="h-4 w-4 mr-2" /> Dispatch
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {/* Confirm-delete dialog for movement rows */}
      {confirmDelete && (
        <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
          <DialogContent data-testid="confirm-delete-move-dialog" className="rounded-2xl max-w-md" aria-describedby="confirm-delete-move-desc">
            <DialogHeader>
              <DialogTitle className="text-red-700">Permanently delete this movement?</DialogTitle>
            </DialogHeader>
            <div id="confirm-delete-move-desc" className="text-sm text-gray-700 space-y-2">
              <div className="rounded-md bg-red-50 border border-red-200 p-3 space-y-1">
                <div><span className="text-xs uppercase tracking-wider text-red-700 font-bold">{confirmDelete.type}</span></div>
                <div className="font-mono">{confirmDelete.item} · {confirmDelete.units} u</div>
                {confirmDelete.store_name && <div>Store: <span className="font-semibold">{confirmDelete.store_name}</span></div>}
                <div className="text-xs text-gray-500">
                  Recorded by {confirmDelete.actor_name} on {confirmDelete.at.slice(0, 19).replace("T", " ")}
                </div>
              </div>
              <p className="text-xs text-gray-600">
                This will REVERSE the balance effect (dispatch → returns to central; receipt → removes from central).
                The row disappears from every summary and tile. One audit trail line is written — that trace is the only place it survives.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
              <Button
                data-testid="confirm-delete-move-btn"
                onClick={() => deleteMovement(confirmDelete)}
                disabled={saving}
                className="bg-red-600 hover:bg-red-700 text-white">
                <Trash2 className="h-4 w-4 mr-2" /> Delete permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {/* Photo preview lightbox */}
      {photoPreview && (
        <div onClick={() => setPhotoPreview(null)}
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-zoom-out"
          data-testid="photo-lightbox">
          <img src={photoPreview} alt="Delivery proof" className="max-h-[90vh] max-w-full rounded-lg shadow-2xl" />
          <button className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            onClick={() => setPhotoPreview(null)} aria-label="Close">
            <XIcon className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, color, icon: Icon, testId }) {
  return (
    <Card data-testid={testId} className="rounded-xl border-gray-200 bg-white p-4">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[11px] uppercase font-bold tracking-[0.14em] text-gray-500">{label}</div>
          <div className={`font-display text-2xl font-black mt-1 truncate ${color}`}>{value}</div>
          {sub && <div className="text-xs text-gray-500 mt-1 truncate">{sub}</div>}
        </div>
        {Icon && <Icon className="h-5 w-5 text-gray-300 shrink-0" />}
      </div>
    </Card>
  );
}
